import { useState } from "react";
import { useSuspenseQuery } from "@apollo/client";
import { createFileRoute } from "@tanstack/react-router";

import { Checkbox } from "@roqua/components/ui/checkbox";
import { Label } from "@roqua/components/ui/label";

import { LabTestResultsRefreshButton } from "@/components/LabObservables/LabTestResultsRefreshButton";
import LabTestResultsTable from "@/components/LabTestResultsTable";
import { gql } from "@/generated/gql";
import { useLabTestResultsRefresh } from "@/hooks/useLabTestResultsRefresh";
import { useTranslation } from "@/i18n/client";

export const Route = createFileRoute("/$organizationKey/$dossierId/lab_test_results")({
  component: RouteComponent,
});

const Query = gql(`
  query LabTestResults($dossierId: ID!) {
    labObservables {
      nodes {
        ...LabObservableFragment
      }
    }

    dossier(id: $dossierId) {
      id
      labTestResults {
        nodes {
          ...LabTestResultFragment
        }
      }
    }
  }
`);

function RouteComponent() {
  const { t } = useTranslation(["LabTestResults"]);
  const { dossierId } = Route.useParams();
  const { data } = useSuspenseQuery(Query, { variables: { dossierId } });
  const [showEmptyRows, setShowEmptyRows] = useState(false);
  const { loading: labFetchLoading, handleRefresh } = useLabTestResultsRefresh();

  const results = data.dossier.labTestResults.nodes;

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t("LabTestResults:title")}</h1>
        <div className="flex items-center gap-3">
          <div>
            <Label className="flex items-center gap-1">
              <Checkbox
                checked={showEmptyRows}
                onCheckedChange={(checked) =>
                  setShowEmptyRows(checked === "indeterminate" ? false : checked === true)
                }
              />
              {t("LabTestResults:controls.showEmptyRows")}
            </Label>
          </div>
          <div>
            <LabTestResultsRefreshButton loading={labFetchLoading} onClick={handleRefresh} />
          </div>
        </div>
      </div>
      <div className="bg-background overflow-x-auto rounded-md p-4 shadow-sm">
        <LabTestResultsTable
          labObservables={data.labObservables.nodes}
          labTestResults={results}
          hideEmptyRows={!showEmptyRows}
        />
      </div>
    </div>
  );
}
